import type { AllarmiPaginationDTO } from '@/models/dto/allarme/allarme-pagination-dto'
import type { AllarmeSconosciutoDTO } from '@/models/dto/allarme/allarme-sconosciuto-dto'
import { mapToAlarms } from '@/models/mappers/alarm/alarms-mapper'
import { mapToUnknownAlarm } from '@/models/mappers/alarm/unknows-alarm-mapper'
import type { AlarmsWithPagination } from '@/models/types/alarm/alarms-with-pagination'
import type { UnknownAlarm } from '@/models/types/alarm/unknown-alarm'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
import type { FetchOptionsType } from './api'
import { deleteJsonRequest, getRequest, patchJsonRequest } from './make-request'

type AlarmGetOptions = {
    order?: OrderType
    sort?: keyof AllarmeSconosciutoDTO
    search?: string
    page?: string
    value?: string
} & Partial<Record<keyof UnknownAlarm, string>>

type AlarmCrudType = (fetchOptions?: FetchOptionsType) => {
    getOne: (id: number) => Promise<UnknownAlarm>
    getAll: () => Promise<UnknownAlarm[]>
    getMany: (
        itemsPerPage?: string,
        options?: AlarmGetOptions,
        onlyEnabled?: boolean,
        fetchOptions?: { cache?: RequestCache; timeout?: number },
    ) => Promise<AlarmsWithPagination>
    getManyActive: (
        itemsPerPage?: string,
        options?: Omit<AlarmGetOptions, 'archived' | 'state'>,
    ) => Promise<AlarmsWithPagination>
    getManyDismissed: (
        itemsPerPage?: string,
        options?: Omit<AlarmGetOptions, 'archived' | 'state'>,
    ) => Promise<AlarmsWithPagination>
    getManyArchived: (
        itemsPerPage?: string,
        options?: Omit<AlarmGetOptions, 'archived' | 'state'>,
    ) => Promise<AlarmsWithPagination>
    // post: (alarm: NewAlarm) => Promise<Alarm>
    // put: (alarm: Alarm) => Promise<Alarm>
    delete: (id: number) => Promise<void>
    dismiss: (id: number, note?: string) => Promise<void>
    archive: (id: number) => Promise<void>
    restore: (id: number) => Promise<void>
    share: (id: number) => Promise<void>
}

const ALARM_URL =
    typeof window === 'undefined' ? `${process.env.API_URL}/api/v1/Allarmi` : `${location.origin}/api/v1/Allarmi`
const ALARM_TAG = 'alarm'

const getMany: (
    itemsPerPage?: string,
    options?: AlarmGetOptions,
    onlyEnabled?: boolean,
    fetchOptions?: FetchOptionsType,
) => Promise<AlarmsWithPagination> = async (itemsPerPage, options, onlyEnabled, fetchOptions) => {
    const url = new URL(`${ALARM_URL}/Pagination`)
    url.searchParams.append('PageSize', itemsPerPage ?? '999999')
    options?.page && url.searchParams.append('PageIndex', options.page)
    options?.order && url.searchParams.append('Descending', options.order === 'desc' ? 'true' : 'false')
    options?.sort && url.searchParams.append('SortBy', capitalizeFirstLetter(options.sort))

    onlyEnabled && url.searchParams.append('OnlyActive', 'true')

    options?.search && url.searchParams.append('Filters[search]', options.search)

    options?.sensorName && url.searchParams.append('Filters[AliasIdSensore]', options.sensorName)
    options?.description && url.searchParams.append('Filters[Descrizione]', options.description)
    options?.state && url.searchParams.append('Filters[Stato]', options.state)
    options?.note && url.searchParams.append('Filters[Note]', options.note)
    options?.timestampstart && url.searchParams.append('Filters[TimestampStart]', options.timestampstart)
    options?.timestamplast && url.searchParams.append('Filters[TimestampLast]', options.timestamplast)
    options?.maxThreshold && url.searchParams.append('Filters[RMaxThreshold]', options.maxThreshold)
    options?.minThreshold && url.searchParams.append('Filters[RMinThreshold]', options.minThreshold)
    options?.value && url.searchParams.append('Filters[Value]', options.value)
    options?.ackTimestamp && url.searchParams.append('Filters[TimestampAck]', options.ackTimestamp)
    options?.facilityId && url.searchParams.append('Filters[IdImpianto]', options.facilityId)
    options?.userId && url.searchParams.append('Filters[IdUtente]', options.userId)
    options?.sensorId && url.searchParams.append('Filters[IdSensore]', options.sensorId)
    options?.archived && url.searchParams.append('Filters[Archiviato]', options.archived)
    options?.shared && url.searchParams.append('Filters[Condiviso]', options.shared)

    const allarmiPaginationDTO = await getRequest<AllarmiPaginationDTO>({
        url,
        next: { tags: [ALARM_TAG] },
        ...fetchOptions,
    })
    const facilitiesWithPagination = mapToAlarms(allarmiPaginationDTO)
    return facilitiesWithPagination
}

const alarmCRUD: AlarmCrudType = (fetchOptions) => ({
    getOne: async (id) => {
        const url = new URL(`${ALARM_URL}/${id.toString()}`)
        const allarmeDTO = await getRequest<AllarmeSconosciutoDTO>({
            url,
            next: { tags: [ALARM_TAG] },
            ...fetchOptions,
        })
        const alarm = mapToUnknownAlarm(allarmeDTO)
        return alarm
    },
    getAll: async () => {
        const url = new URL(ALARM_URL)
        const allarmeDTOs = await getRequest<AllarmeSconosciutoDTO[]>({
            url,
            next: { tags: [ALARM_TAG] },
            ...fetchOptions,
        })
        const alarms = allarmeDTOs.map((allarmeDTO) => mapToUnknownAlarm(allarmeDTO))
        return alarms
    },
    getMany,
    getManyActive: async (itemsPerPage, options) => getMany(itemsPerPage, { ...options }, true, { cache: 'no-cache' }),
    getManyDismissed: async (itemsPerPage, options) =>
        getMany(itemsPerPage, { ...options, state: '0', archived: 'false' }, false, {
            cache: 'no-cache',
        }),
    getManyArchived: async (itemsPerPage, options) =>
        getMany(itemsPerPage, { ...options, archived: 'true' }, false, { cache: 'no-cache' }),
    // post: async (newAlarm) => {
    //     const nuovoAllarmeDTO = mapToNuovoAllarmeDTO(newAlarm)
    //     const url = new URL(ALARM_URL)
    //     const allarmeDTOResponse = await postJsonRequest<AllarmeDTO>({ url, body: nuovoAllarmeDTO, ...fetchOptions })
    //     const alarm = mapToAlarm(allarmeDTOResponse)
    //     return alarm
    // },
    // put: async (alarm) => {
    //     const allarmeDTO = mapToAllarmeDTO(alarm)
    //     const url = new URL(ALARM_URL)
    //     const allarmeDTOResponse = await putJsonRequest<AllarmeDTO>({ url, body: allarmeDTO, ...fetchOptions })
    //     const updatedAlarm = mapToAlarm(allarmeDTOResponse)
    //     return updatedAlarm
    // },
    delete: async (id) => {
        const url = new URL(`${ALARM_URL}/${id.toString()}`)
        await deleteJsonRequest({ url, ...fetchOptions })
    },
    dismiss: async (id, note) => {
        const url = new URL(`${ALARM_URL}/UpdateAct/${id.toString()}`)
        await patchJsonRequest({ url, ...(note && { body: note }), ...fetchOptions })
    },
    archive: async (id) => {
        const url = new URL(`${ALARM_URL}/AchiviaAllarme/${id.toString()}/true`)
        await patchJsonRequest({ url, ...fetchOptions })
    },
    restore: async (id) => {
        const url = new URL(`${ALARM_URL}/AchiviaAllarme/${id.toString()}/false`)
        await patchJsonRequest({ url, ...fetchOptions })
    },
    share: async (id) => {
        const url = new URL(`${ALARM_URL}/CondividiAllarme/${id.toString()}`)
        await patchJsonRequest({ url, ...fetchOptions })
    },
})

export { ALARM_TAG, alarmCRUD, type AlarmCrudType, type AlarmGetOptions }
